import React from "react";
import { useNavigate } from "react-router-dom";
import { Box, ButtonBase, Typography } from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import HowToRegOutlinedIcon from "@mui/icons-material/HowToRegOutlined";
import MonitorHeartOutlinedIcon from "@mui/icons-material/MonitorHeartOutlined";
import { useAuth } from "../../common/auth";
import { ROUTES } from "../../common/constant/routes";
import { SalesResponse, getSalesOrders } from "../../common/requests/sales/endpoints";
import { TrafficResponse, getTraffic } from "../../common/requests/platform/endpoints";
import { C } from "../../common/theme";
import { formatCount, formatMoney } from "../../common/utils/format";
import { useCounts } from "../../components/Layout/counts";
import { ErrorState, KpiCard, KpiStrip, LoadingRows, PageShell, Panel, Section, StatusPill, useResource } from "../../ui";
import LifetimeStrip from "../Sales/components/LifetimeStrip";
import { change, flattenOrders, formatDate, inRange, resolveRange, summarise } from "../Sales/salesData";
import { InboxItem, hasInboxSources, inboxItems } from "./overviewData";

const ICONS: Record<InboxItem["key"], React.ReactNode> = {
  orders: <ReceiptLongOutlinedIcon />,
  payouts: <AccountBalanceWalletOutlinedIcon />,
  applications: <HowToRegOutlinedIcon />,
  services: <MonitorHeartOutlinedIcon />,
};

function InboxRow({ item, onOpen }: { item: InboxItem; onOpen: () => void }) {
  const danger = item.tone === "danger";
  return (
    <ButtonBase
      onClick={onOpen}
      sx={{
        width: "100%",
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        px: { xs: 1.5, sm: 2 },
        py: 1.5,
        minHeight: 64,
        textAlign: "left",
        justifyContent: "flex-start",
        borderTop: `1px solid ${C.line}`,
        "&:first-of-type": { borderTop: 0 },
        "&:hover": { bgcolor: C.head },
        "&:focus-visible": { outline: `2px solid ${C.coral}`, outlineOffset: -2 },
      }}
    >
      <Box sx={{ width: 40, height: 40, borderRadius: "8px", display: "grid", placeItems: "center", flex: "none", bgcolor: danger ? C.dangerTint : C.bone, color: danger ? C.danger : C.coralText, "& svg": { fontSize: 20 } }}>
        {ICONS[item.key]}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body1" sx={{ fontWeight: 700 }}>
          {item.text}
        </Typography>
        <Typography variant="caption" sx={{ color: C.muted, display: "block" }} noWrap>
          {item.detail}
        </Typography>
      </Box>
      <ChevronRightIcon sx={{ color: C.faint }} />
    </ButtonBase>
  );
}

/** The landing page: what needs you now (counted on the server), then how
 * the business is going. Each part only shows for admins who can open the
 * page it links to. */
export default function Overview() {
  const { idToken, adminUser, hasPermission } = useAuth();
  const navigate = useNavigate();
  const counts = useCounts();
  const isSuperadmin = adminUser?.role === "superadmin";
  const canSales = hasPermission("sales");
  const ready = !!idToken;

  const sales = useResource<SalesResponse>(async () => (await getSalesOrders(idToken!)).data, [idToken], { enabled: ready && canSales, errorText: "Couldn't load sales." });
  const traffic = useResource<TrafficResponse>(
    async () => {
      const end = new Date();
      const start = new Date(end.getTime() - 30 * 86400000);
      return (await getTraffic(idToken!, { start: start.toISOString(), end: end.toISOString(), tz: Intl.DateTimeFormat().resolvedOptions().timeZone })).data;
    },
    [idToken],
    { enabled: ready && isSuperadmin }
  );

  const salesView = React.useMemo(() => {
    if (!sales.data) return null;
    const lines = flattenOrders(sales.data.orders);
    const range = resolveRange("30d", undefined, undefined);
    const current = summarise(lines.filter((l) => inRange(l, range.start, range.end)));
    const previous = range.previous ? summarise(lines.filter((l) => inRange(l, range.previous!.start, range.previous!.end))) : null;
    return { lifetime: summarise(lines), current, previous, currency: sales.data.reporting_currency };
  }, [sales.data]);
  const d = (pick: (s: NonNullable<typeof salesView>["current"]) => number | null) =>
    salesView?.previous ? change(pick(salesView.current), pick(salesView.previous)) : null;

  const items = counts.data ? inboxItems(counts.data) : [];
  const showInbox = !counts.data || hasInboxSources(counts.data);
  const c = counts.data;

  return (
    <PageShell
      title="Overview"
      subtitle={`${formatDate(new Date())}${adminUser?.display_name ? ` · Signed in as ${adminUser.display_name}` : ""}`}
    >
      {showInbox && (
        <Section
          title="Needs you"
          actions={items.length > 0 ? <StatusPill tone="warning">{items.reduce((n, i) => n + i.count, 0)} open</StatusPill> : undefined}
        >
          <Panel padding={0} sx={{ overflow: "hidden" }}>
            {counts.status === "error" && !counts.data ? (
              <ErrorState message={counts.error} onRetry={counts.reload} />
            ) : !counts.data ? (
              <LoadingRows rows={2} height={48} />
            ) : items.length === 0 ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, px: 2, py: 2 }}>
                <Box sx={{ width: 40, height: 40, borderRadius: "8px", display: "grid", placeItems: "center", bgcolor: C.leafTint, color: C.leaf }}>
                  <CheckCircleOutlineIcon />
                </Box>
                <Box>
                  <Typography variant="body1" sx={{ fontWeight: 700 }}>
                    All clear
                  </Typography>
                  <Typography variant="caption" sx={{ color: C.muted }}>
                    No payouts, stuck orders, applications or service issues waiting.
                  </Typography>
                </Box>
              </Box>
            ) : (
              items.map((item) => <InboxRow key={item.key} item={item} onOpen={() => navigate(item.to)} />)
            )}
          </Panel>
        </Section>
      )}

      {canSales && (
        <Section
          title="Sales · last 30 days"
          actions={
            <ButtonBase onClick={() => navigate(ROUTES.SALES)} sx={{ fontSize: 13.5, fontWeight: 700, minHeight: 40, px: 1, borderRadius: "6px", color: C.coralText }}>
              Open Sales <ChevronRightIcon sx={{ fontSize: 18 }} />
            </ButtonBase>
          }
        >
          {sales.status === "error" && !sales.data ? (
            <Panel>
              <ErrorState message={sales.error} onRetry={sales.reload} />
            </Panel>
          ) : !salesView ? (
            <LoadingRows rows={2} height={84} />
          ) : (
            <>
              <KpiStrip>
                <KpiCard title="GMV" value={formatMoney(salesView.current.gmv, salesView.currency)} delta={d((s) => s.gmv)} deltaLabel="vs prev. 30d" onClick={() => navigate(ROUTES.SALES + "?range=30d")} />
                <KpiCard title="Orders" value={formatCount(salesView.current.orders)} delta={d((s) => s.orders)} deltaLabel="vs prev. 30d" sub={`${formatCount(salesView.current.units)} units`} />
                <KpiCard title="Platform revenue" value={formatMoney(salesView.current.platformRevenue, salesView.currency)} delta={d((s) => s.platformRevenue)} deltaLabel="vs prev. 30d" />
                <KpiCard title="Buyers" value={formatCount(salesView.current.buyers)} delta={d((s) => s.buyers)} deltaLabel="vs prev. 30d" sub={`${formatCount(salesView.current.newBuyers)} new`} />
              </KpiStrip>
              <Typography variant="overline" component="h3" sx={{ color: C.muted, mt: 1, mb: -0.5 }}>
                All time
              </Typography>
              <LifetimeStrip summary={salesView.lifetime} currency={salesView.currency} />
            </>
          )}
        </Section>
      )}

      {isSuperadmin && (
        <Section title="Platform & community">
          <KpiStrip>
            <KpiCard
              title="Services healthy"
              value={c?.services ? `${c.services.total - c.services.issues} / ${c.services.total}` : "…"}
              tone={c?.services && c.services.issues > 0 ? "warning" : "default"}
              onClick={() => navigate(ROUTES.PLATFORM)}
            />
            <KpiCard
              title="Page views today"
              value={traffic.data ? formatCount(traffic.data.summary.today_views) : traffic.status === "error" ? "–" : "…"}
              sub={traffic.data ? `${formatCount(traffic.data.summary.views)} in 30 days` : undefined}
              onClick={() => navigate(ROUTES.PLATFORM)}
            />
            <KpiCard title="New chats (24h)" value={c?.chats_new_24h !== null && c?.chats_new_24h !== undefined ? formatCount(c.chats_new_24h) : "…"} onClick={() => navigate(ROUTES.CHAT_MONITOR)} />
            <KpiCard
              title="Pending payouts"
              value={c?.payouts_pending ? formatCount(c.payouts_pending.count) : "…"}
              sub={c?.payouts_pending?.count ? c.payouts_pending.totals.map((t) => formatMoney(t.amount, t.currency)).join(" + ") : undefined}
              onClick={() => navigate(ROUTES.WITHDRAWALS)}
            />
          </KpiStrip>
        </Section>
      )}

      {!canSales && !isSuperadmin && !hasPermission("withdrawals") && (
        <Typography variant="body2" sx={{ color: C.muted }}>
          Use the menu to open the sections you have access to.
        </Typography>
      )}
    </PageShell>
  );
}
